import type { ActivityEvent, AgentJob, Evidence, PortfolioSnapshot, PrimaryResearchTask, Venture } from "../shared/types";

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: { "Content-Type": "application/json", "x-xwarm-csrf": "1", ...(options?.headers || {}) },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error || `Request failed with ${response.status}`);
  }
  return (await response.json()) as T;
}

export const api = {
  session: () => request<{ authenticated: boolean }>("/api/auth/session"),
  login: (password: string) =>
    request<{ authenticated: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ authenticated: boolean }>("/api/auth/logout", { method: "POST" }),
  snapshot: () => request<PortfolioSnapshot>("/api/snapshot"),
  setRuntime: (action: "pause" | "resume", reason: string) =>
    request<{ control: { paused: boolean; reason: string | null }; runtime: PortfolioSnapshot["runtime"] }>("/api/runtime/control", {
      method: "POST",
      body: JSON.stringify({ action, reason }),
    }),
  venture: (slug: string) =>
    request<{ venture: Venture; evidence: Evidence[]; primaryResearch: PrimaryResearchTask[]; jobs: AgentJob[]; events: ActivityEvent[] }>(`/api/ventures/${slug}`),
  preparePrimaryResearch: (slug: string) =>
    request<{ tasks: PrimaryResearchTask[] }>(`/api/ventures/${slug}/primary-research/plan`, { method: "POST" }),
  updatePrimaryResearchCompliance: (slug: string, taskId: string, payload: Record<string, unknown>) =>
    request<{ task: PrimaryResearchTask }>(`/api/ventures/${slug}/primary-research/${taskId}/compliance`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  recordPrimaryResearchSource: (slug: string, taskId: string, payload: Record<string, unknown>) =>
    request<{ task: PrimaryResearchTask }>(`/api/ventures/${slug}/primary-research/${taskId}/source`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updatePrimaryResearchPermission: (slug: string, taskId: string, payload: Record<string, unknown>) =>
    request<{ task: PrimaryResearchTask }>(`/api/ventures/${slug}/primary-research/${taskId}/permission`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  addOperatingMetric: (slug: string, payload: Record<string, unknown>) =>
    request<{ metricId: string; venture: Venture }>(`/api/ventures/${slug}/operating-metrics`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  runNext: (slug: string) => request<{ job: AgentJob }>(`/api/ventures/${slug}/run-next`, { method: "POST" }),
  addEvidence: (slug: string, payload: Record<string, unknown>) =>
    request<{ evidenceId: string; evaluation: unknown }>(`/api/ventures/${slug}/evidence`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  decideGate: (slug: string, step: number, decision: "pass" | "revise" | "kill", note: string) =>
    request<{ decision: string; evaluation: unknown; nextStep?: number | null }>(`/api/ventures/${slug}/steps/${step}/decision`, {
      method: "POST",
      body: JSON.stringify({ decision, note }),
    }),
};
